import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/apiClient";
import type { Location } from "../api/types";
import { ReceiptBuilder } from "../components/receipt/ReceiptBuilder";
import { ReceiptPreview } from "../components/receipt/ReceiptPreview";
import { Select } from "../components/ui/Select";
import { PrinterCard, jobOptionsFor } from "../components/receipt/PrinterCard";
import { toCatPrinterJob } from "../receipt/catPrinter";
import type { ReceiptData } from "../receipt/receiptLayout";
import { renderReceipt, toMonoBitmap } from "../receipt/receiptRender";
import {
  DEFAULT_TEMPLATE,
  templateFromStored,
  type ReceiptTemplate,
} from "../receipt/receiptTemplate";

/**
 * G-64 — where a shop designs its ticket and a counter pairs its printer.
 *
 * ONE PAGE, TWO SCOPES, and the headings say which is which. The design belongs
 * to the SHOP — a replaced tablet keeps it, and Còrsega can differ from Sant
 * Joan. The printer belongs to THIS TABLET, because it is the one physically on
 * this counter. Left unlabelled, someone would eventually assume that forgetting
 * the printer also wipes their design.
 *
 * They share a page because they share a loop: change a block, watch the
 * preview, print it, hold the paper. Split across two screens you would walk
 * between them on every adjustment.
 */

/** Invented on purpose — designing a receipt must never touch a real sale. */
const SAMPLE_LINES: ReceiptData["lines"] = [
  {
    description: "Ramo de temporada",
    quantity: "1",
    uom: "UNIT",
    unitPriceEur: "24.00",
    lineTotalEur: "24.00",
  },
  {
    description: "Vela perfumada",
    quantity: "2",
    uom: "UNIT",
    unitPriceEur: "6.50",
    lineTotalEur: "13.00",
  },
];

export function ReceiptPrinterPage() {
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationId, setLocationId] = useState<string>("");
  const [template, setTemplate] = useState<ReceiptTemplate | null>(null);
  const [saved, setSaved] = useState<ReceiptTemplate | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoImage, setLogoImage] = useState<HTMLImageElement | null>(null);

  const [busy, setBusy] = useState<"load" | "save" | "logo" | null>("load");
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const location = locations.find((l) => l.id === locationId) ?? null;

  /** The sample, wearing THIS shop's name — a preview of someone else's shop
   *  tells you nothing about how your own name wraps at 384 dots. */
  const sample = useMemo<ReceiptData>(
    () => ({
      saleId: "sample",
      soldAt: new Date().toISOString(),
      shop: {
        name: location?.name ?? "Flower World",
        address: location?.address ?? "",
        nif: location?.nif ?? null,
      },
      servedBy: "Ana",
      payments: [{ method: "CASH", amountEur: "37.00" }],
      lines: SAMPLE_LINES,
      totalEur: "37.00",
    }),
    [location?.name, location?.address, location?.nif],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await api.listLocations();
        if (cancelled) return;
        setLocations(rows);
        setLocationId((current) => current || rows[0]?.id || "");
        if (rows.length === 0) {
          // Otherwise every effect below bails on the empty id and the card
          // sits on "Loading the design…" for ever, blaming the wrong thing.
          setError(
            "There are no shops yet. Create one before designing a receipt.",
          );
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Could not load shops.",
          );
        }
      } finally {
        if (!cancelled) setBusy(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!locationId) return;
    let cancelled = false;
    setBusy("load");
    void (async () => {
      try {
        const stored = await api.getReceiptTemplate(locationId);
        if (cancelled) return;
        // A shop that has never opened the builder gets the default rather than
        // an empty ticket, keeping its own logo settings; one rule with the
        // kiosk's print (G-156), repairing anything stored by an older renderer.
        const next = templateFromStored(stored);
        setTemplate(next);
        setSaved(next);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Could not load the design.",
          );
        }
      } finally {
        if (!cancelled) setBusy(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  /**
   * Fetch the logo bytes and hand back an object URL.
   *
   * Revoking happens in the effect below, NOT inside a `setState` updater — an
   * updater must be pure, and React may call it more than once for a single
   * update (StrictMode does exactly that), which would revoke unpredictably.
   */
  const reloadLogo = useCallback(async (id: string) => {
    setLogoUrl(await api.fetchReceiptLogoUrl(id));
  }, []);

  useEffect(() => {
    if (!locationId) return;
    void reloadLogo(locationId);
  }, [locationId, reloadLogo]);

  /**
   * One owner of every object URL: whenever `logoUrl` is replaced or the page
   * unmounts, the URL it held is released. Without this each visit left a logo
   * blob resident for the life of the tab — and OD-35 says the shop tablet is
   * never logged out, so it never gets a fresh one.
   */
  useEffect(() => {
    if (!logoUrl) return;
    return () => URL.revokeObjectURL(logoUrl);
  }, [logoUrl]);

  // The renderer draws an <img>, not a URL, so decode it before it is needed —
  // drawing an undecoded image silently produces a blank space.
  useEffect(() => {
    if (!logoUrl) {
      setLogoImage(null);
      return;
    }
    const img = new Image();
    img.onload = () => setLogoImage(img);
    img.onerror = () => setLogoImage(null);
    img.src = logoUrl;
  }, [logoUrl]);

  const dirty = useMemo(
    () => JSON.stringify(template) !== JSON.stringify(saved),
    [template, saved],
  );

  const handleSave = async () => {
    if (!template || !locationId) return;
    setBusy("save");
    setError(null);
    setNote(null);
    try {
      await api.saveReceiptTemplate(locationId, {
        blocks: template.blocks,
        logoScale: template.logoScale,
      });
      setSaved(template);
      setNote("Saved. Every receipt this shop prints will look like this.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not save.");
    } finally {
      setBusy(null);
    }
  };

  const handleUploadLogo = async (file: File) => {
    if (!locationId) return;
    setBusy("logo");
    setError(null);
    setNote(null);
    try {
      const stored = await api.uploadReceiptLogo(locationId, file);
      setTemplate((t) => (t ? { ...t, logoKey: stored.logoKey } : t));
      setSaved((t) => (t ? { ...t, logoKey: stored.logoKey } : t));
      await reloadLogo(locationId);
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Could not upload the logo.",
      );
    } finally {
      setBusy(null);
    }
  };

  const handleClearLogo = async () => {
    if (!locationId) return;
    setBusy("logo");
    setError(null);
    try {
      await api.clearReceiptLogo(locationId);
      setTemplate((t) => (t ? { ...t, logoKey: null } : t));
      setSaved((t) => (t ? { ...t, logoKey: null } : t));
      await reloadLogo(locationId);
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Could not remove the logo.",
      );
    } finally {
      setBusy(null);
    }
  };

  /**
   * The bytes a test print puts on paper: the design in FRONT of you, not the
   * saved one — a test print is for checking a change before committing to it.
   *
   * Both cards print the same known-good receipt. On the sticker printer that
   * is deliberate for now: printing something already proven separates "the
   * transport reaches this second machine" from "our note renderer is right",
   * which are different problems and should not be debugged together.
   */
  const buildTestJob = useMemo(() => {
    if (!template) return null;
    return (printer: Parameters<typeof jobOptionsFor>[0]) => {
      const canvas = renderReceipt(template, sample, logoImage);
      return toCatPrinterJob(toMonoBitmap(canvas), jobOptionsFor(printer));
    };
  }, [template, sample, logoImage]);

  return (
    <section className="page">
      <header className="page-head">
        <h1 className="page-title">Receipts</h1>
        <p className="page-subtitle">
          Design the ticket this shop prints, and connect the printer on this
          counter.
        </p>
      </header>

      {error ? <div className="alert error">{error}</div> : null}
      {note ? <div className="alert">{note}</div> : null}

      <div className="receipt-workspace">
        <div className="receipt-workspace-grid">
          <div className="receipt-workspace-editor">
            <div className="card">
              <div className="receipt-card-head">
                <h2>This shop</h2>
                {locations.length > 1 ? (
                  <Select
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                    aria-label="Which shop's receipt to design"
                  >
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </Select>
                ) : null}
              </div>
              <p className="muted">
                The design belongs to the shop, so a replaced tablet keeps it.
              </p>

              {template ? (
                <>
                  <ReceiptBuilder
                    template={template}
                    onChange={setTemplate}
                    logoUrl={logoUrl}
                    onUploadLogo={(f) => void handleUploadLogo(f)}
                    onClearLogo={() => void handleClearLogo()}
                    busy={busy !== null}
                  />
                  <div className="button-row">
                    <button
                      type="button"
                      className="button"
                      onClick={() => void handleSave()}
                      disabled={busy !== null || !dirty}
                    >
                      {busy === "save"
                        ? "Saving…"
                        : dirty
                          ? "Save the design"
                          : "Saved"}
                    </button>
                    <button
                      type="button"
                      className="button plain"
                      onClick={() =>
                        setTemplate((t) =>
                          t ? { ...DEFAULT_TEMPLATE, logoKey: t.logoKey } : t,
                        )
                      }
                      disabled={busy !== null}
                    >
                      Start from the default
                    </button>
                  </div>
                </>
              ) : (
                <p className="muted">Loading the design…</p>
              )}
            </div>

            <PrinterCard
              role="receipts"
              title="Ticket printer"
              hint="The one loaded with till roll. Paired to this tablet, not to your account."
              buildTestJob={buildTestJob}
            />

            {/* OPTIONAL, and said so plainly. Most counters run one printer and
                swap the roll (owner, 2026-09-12) — notes fall back to the
                receipt printer on their own, so an empty card here is a choice
                rather than something left undone. */}
            <PrinterCard
              role="notes"
              title="Sticker printer (optional)"
              hint="A second machine, kept loaded with sticker paper so nobody has to swap the roll."
              unpairedHint="Not paired — gift notes print from the receipt printer, on whatever roll is loaded."
              buildTestJob={buildTestJob}
              testLabel="Print a test sticker"
            />
          </div>

          <div className="receipt-workspace-preview">
            <div className="card">
              <h2>What will print</h2>
              <p className="muted">
                Shown exactly as the printer receives it: 384 dots wide, pure
                black and white, no greys.
              </p>
              {template ? (
                <ReceiptPreview
                  template={template}
                  data={sample}
                  logo={logoImage}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
